// SNOWCLUB-V101-PROD-ACCOUNT-HANDOFF-20260927-001 — CARRY THE CURRENT ROOT SESSION INTO THE ACCOUNTS APP.
//
// THE GAP: root signs in through /api/auth/login and holds the Strapi JWT in cart-store/localStorage.
// On www.snowbus.com the accounts app lives on accounts.mysnowclub.com, a different registrable domain,
// so the `sb_access` cookie root writes is never sent there and every My Trips / Profile / My Orders link
// lands on accounts/login?next=… for a member who is already signed in.
//
// THE MECHANISM is the accounts app's own /bridge popup, completed for current Strapi sessions:
//   root (user click) ─ window.open(accounts/bridge?state=S) ─▶ popup
//   popup ─ {need-token, state:S} ─▶ root         (targetOrigin = root origin, never "*")
//   root  ─ {token, state:S, token:JWT} ─▶ popup  (only after the reply above validated; targetOrigin = accounts origin)
//   popup ─ POST same-origin /api/auth/bridge (server verifies the JWT with Strapi, sets sb_access)
//   popup ─ {ok, state:S} ─▶ root ─ navigates THIS tab to the allowlisted accounts path, closes the popup.
//
// Every inbound message must match the exact accounts origin AND the exact popup handle AND the state
// (MDN postMessage / OWASP HTML5 Security: origin alone is spoofable by any window that framed or
// navigated the popup, and source alone is not an origin check). The JWT never appears in a URL, in
// history, or in a log line. The state is a one-use nonce, not a secret credential: it rides the popup URL
// so the popup can echo it, and is dead the moment the handoff ends.
//
// Pure of `window`: every side effect comes through `HandoffIO`, so the decision logic is unit-tested with
// spoofed origins/sources and the real cross-origin behaviour is exercised in a browser fixture.

/** Accounts destinations a handoff may land on. Anything else is never bridged and never navigated to. */
export const ACCOUNT_HANDOFF_PATHS = ["/account", "/my-orders", "/trips"] as const;

export const HANDOFF_TIMEOUT_MS = 15_000;
const CLOSED_GRACE_MS = 600;
const MAX_JWT_LENGTH = 4096;

export const MSG = {
  needToken: "snowclub-auth:need-token",
  token: "snowclub-auth:token",
  ok: "snowclub-auth:ok",
  failed: "snowclub-auth:failed",
  error: "snowclub-auth:error",
} as const;

export interface PopupHandle {
  closed: boolean;
  close(): void;
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface HandoffMessageEvent {
  origin: string;
  source: unknown;
  data: unknown;
}

export interface HandoffIO {
  /** Exact accounts origin, e.g. https://accounts.mysnowclub.com (URL.origin, no trailing slash). */
  accountsOrigin: string;
  /** Opens the popup synchronously (must run inside the user's click). null = blocked. */
  open(url: string): PopupHandle | null;
  /** Installs the window `message` listener; returns its remover. */
  listen(handler: (ev: HandoffMessageEvent) => void): () => void;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(id: unknown): void;
  /** Cryptographically random one-use state. */
  randomState(): string;
  /** The CURRENT client JWT, read at reply time (not captured at click). */
  getJwt(): string | null;
  /** Navigate this tab (used for both success and the login fallback). */
  navigate(url: string): void;
}

export type HandoffOutcome = "success" | "fallback" | "cancelled";

export interface HandoffResult {
  cancel(): void;
  done: Promise<HandoffOutcome>;
}

export function isWellShapedJwt(t: unknown): t is string {
  return typeof t === "string" && t.length > 0 && t.length <= MAX_JWT_LENGTH && !/\s/.test(t);
}

/** `/my-orders/` → `/my-orders`; returns the allowlisted pathname or null. Query/hash are dropped on purpose. */
export function allowlistedAccountPath(pathname: string): (typeof ACCOUNT_HANDOFF_PATHS)[number] | null {
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return (ACCOUNT_HANDOFF_PATHS as readonly string[]).includes(p)
    ? (p as (typeof ACCOUNT_HANDOFF_PATHS)[number])
    : null;
}

/** The accounts path a URL points at, iff it is on the accounts origin and allowlisted. */
export function accountTargetOf(href: string, accountsOrigin: string): (typeof ACCOUNT_HANDOFF_PATHS)[number] | null {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return null;
  }
  if (u.origin !== accountsOrigin) return null;
  return allowlistedAccountPath(u.pathname);
}

function parentDomain(host: string): string {
  const parts = host.split(".");
  return parts.length > 2 ? parts.slice(1).join(".") : host;
}

/**
 * True when the shared `sb_access` cookie root writes is already visible to the accounts host, so a direct
 * link is valid and no bridge is needed: identical hostname (ports don't matter to cookies — localhost dev),
 * one host a subdomain of the other (stg.mysnowclub.com → accounts.stg.mysnowclub.com), or siblings under
 * one parent (www.mysnowclub.com → accounts.mysnowclub.com). www.snowbus.com → accounts.mysnowclub.com is
 * none of these, which is exactly the cross-origin case the bridge exists for.
 */
export function sharesCookieScope(rootHost: string, accountsHost: string): boolean {
  const a = rootHost.toLowerCase();
  const b = accountsHost.toLowerCase();
  if (!a || !b) return false;
  if (a === b || a.endsWith(`.${b}`) || b.endsWith(`.${a}`)) return true;
  const pa = parentDomain(a);
  return pa.includes(".") && pa === parentDomain(b);
}

// ── CUSTOMER SSO (new mode) ─────────────────────────────────────────────────────────────────────────────────────────
// With customer SSO on, none of the popup/postMessage machinery below is used. The Accounts transfer is a plain same-tab
// navigation to Accounts' own relying-party start, which runs the standard code + PKCE flow through the first-party issuer: a valid
// issuer session means no credential prompt at all, and no token is ever passed through the page, a URL or a message. The legacy
// popup bridge below stays only for the feature-off path and is not the new SSO.

/** The Accounts RP start for an allowlisted Accounts path. The path is only a destination hint; Accounts validates it again. */
export function ssoStartUrl(accountsOrigin: string, path: (typeof ACCOUNT_HANDOFF_PATHS)[number]): string {
  return `${accountsOrigin}/api/auth/sso/login?returnTo=${encodeURIComponent(path)}`;
}

/** Same-tab transfer. Called from a click or the design's account hook; navigation is the only side effect. */
export function startSsoAccountTransfer(
  io: Pick<HandoffIO, "accountsOrigin" | "navigate">,
  path: (typeof ACCOUNT_HANDOFF_PATHS)[number],
): void {
  io.navigate(ssoStartUrl(io.accountsOrigin, path));
}

/** The slice of `Response` the inline flow uses, so the same code runs on the browser's fetch and in tests. */
export interface InlineResponse {
  ok: boolean;
  status: number;
  url: string;
  json(): Promise<unknown>;
  body?: { cancel?: () => unknown } | null;
}

export type InlineFetch = (
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string; redirect?: "follow"; credentials?: "same-origin"; cache?: "no-store" },
) => Promise<InlineResponse>;

const INTERACTION_PATH = /^\/sso\/interaction\/([A-Za-z0-9_-]{8,64})$/;
const SSO_FAILURE_TEXT: Record<string, string> = {
  unavailable: "Sign-in is temporarily unavailable. Please try again.",
};
export const SSO_GENERIC_FAILURE = "We couldn't sign you in. Please try again.";

/**
 * The first credential prompt of a SnowBus visitor, inside the existing popup and on the page they are on. Everything is same-origin
 * fetch with redirects followed, and the provider's own redirects drive everything:
 *   1. `/api/auth/sso/login?mode=inline` → the standard RP start; with no issuer session the chain ends at the provider's
 *      interaction URL (`/sso/interaction/{uid}`); with one it ends at the non-secret `done` endpoint;
 *   2. POST the credentials to `/sso/interaction/{uid}/login` on this origin only → the redirect chain (resume → callback →
 *      `done`) ends in `{ ok }`, or comes back to the interaction page with a fixed `error` code.
 * Every response must still be on this origin; a foreign hop fails the flow closed. No token is read, returned or stored: the
 * session cookie is set httpOnly by the callback, and the caller reads the verified session from `/api/auth/session` afterwards.
 */
export async function inlineSsoLogin({
  fetch: f,
  origin,
  returnTo,
  identifier,
  password,
}: {
  fetch: InlineFetch;
  origin: string;
  returnTo: string;
  identifier: string;
  password: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const fail = (error = SSO_GENERIC_FAILURE) => ({ ok: false as const, error });
  const here = (res: InlineResponse) => {
    try {
      return new URL(res.url);
    } catch {
      return null;
    }
  };
  const same = (res: InlineResponse) => here(res)?.origin === origin;
  const read = async (res: InlineResponse) => {
    try {
      return (await res.json()) as { ok?: unknown; error?: unknown } | null;
    } catch {
      return null;
    }
  };
  const finished = async (res: InlineResponse): Promise<{ ok: true } | { ok: false; error: string }> => {
    if (!same(res) || here(res)?.pathname !== "/api/auth/sso/done") return fail();
    const body = await read(res);
    if (body?.ok === true) return { ok: true };
    return fail(typeof body?.error === "string" ? (SSO_FAILURE_TEXT[body.error] ?? SSO_GENERIC_FAILURE) : SSO_GENERIC_FAILURE);
  };
  const base = { redirect: "follow", credentials: "same-origin", cache: "no-store" } as const;

  try {
    const start = await f(`${origin}/api/auth/sso/login?mode=inline&returnTo=${encodeURIComponent(returnTo)}`, base);
    if (!same(start)) return fail();
    const arrived = here(start);
    if (!arrived) return fail();
    if (arrived.pathname === "/api/auth/sso/done") return await finished(start); // an issuer session already existed
    const uid = INTERACTION_PATH.exec(arrived.pathname)?.[1];
    start.body?.cancel?.(); // the interaction page itself is not needed; the official JSON API below is
    if (!uid) return fail();

    // The provider's interaction cookie is scoped to the interaction URL's path (/sso/interaction/{uid}) — the browser would NOT send
    // it to /api/oidc/interaction/... — so the credentials go to the form target under that very path. That same-origin Root route
    // reads the official interaction (CSRF, prompt) server-side with the browser's own cookie, and answers with the provider's
    // continuation, which this fetch follows to `done`. A refusal comes back as the interaction page with a fixed `error` code.
    const submitted = await f(`${origin}/sso/interaction/${uid}/login`, {
      ...base,
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded", accept: "application/json" },
      body: new URLSearchParams({ identifier, password }).toString(),
    });
    if (!same(submitted)) return fail();
    const landed = here(submitted);
    if (!landed) return fail();
    if (landed.pathname === "/api/auth/sso/done") return await finished(submitted);
    if (landed.pathname === `/sso/interaction/${uid}`) {
      submitted.body?.cancel?.();
      const reason = landed.searchParams.get("error");
      if (reason === "invalid") return fail("Invalid email or password");
      if (reason === "rate_limited") return fail("Too many attempts. Please wait a moment and try again.");
    }
    return fail();
  } catch {
    return fail(); // a network failure, or a cross-origin hop the browser refused: fail closed
  }
}

/** Where the popup-blocked / timed-out / rejected path goes: the accounts login page, back to the target. */
export function loginFallbackUrl(accountsOrigin: string, path: string): string {
  return `${accountsOrigin}/login?next=${encodeURIComponent(path)}`;
}

let active: HandoffResult | null = null;

/** Cancel any in-flight handoff (component unmount, or a newer click superseding an older one). */
export function cancelActiveHandoff(): void {
  active?.cancel();
}

/**
 * Start a handoff. MUST be called synchronously inside the user's click so `open` is not popup-blocked.
 * The listener is installed BEFORE the popup is opened so no reply can be missed.
 */
export function startAccountHandoff(io: HandoffIO, path: (typeof ACCOUNT_HANDOFF_PATHS)[number]): HandoffResult {
  cancelActiveHandoff();

  const state = io.randomState();
  const target = `${io.accountsOrigin}${path}`;
  let popup: PopupHandle | null = null;
  let unlisten: (() => void) | null = null;
  let timeout: unknown = null;
  let closedPoll: unknown = null;
  let sentToken = false;
  let finished = false;
  let resolve!: (o: HandoffOutcome) => void;
  const done = new Promise<HandoffOutcome>((r) => {
    resolve = r;
  });

  const teardown = () => {
    unlisten?.();
    unlisten = null;
    if (timeout !== null) io.clearTimer(timeout);
    if (closedPoll !== null) io.clearTimer(closedPoll);
    timeout = closedPoll = null;
    try {
      if (popup && !popup.closed) popup.close();
    } catch {
      /* already gone */
    }
  };
  const finish = (outcome: HandoffOutcome, then?: () => void) => {
    if (finished) return;
    finished = true;
    teardown();
    if (active === result) active = null;
    then?.();
    resolve(outcome);
  };
  const fallback = () => finish("fallback", () => io.navigate(loginFallbackUrl(io.accountsOrigin, path)));

  const result: HandoffResult = { cancel: () => finish("cancelled"), done };
  active = result;

  unlisten = io.listen((ev) => {
    if (finished) return;
    // BOTH checks, always: the exact accounts origin AND the exact popup we opened.
    if (ev.origin !== io.accountsOrigin) return;
    if (!popup || ev.source !== popup) return;
    const d = ev.data as { type?: unknown; state?: unknown } | null;
    if (!d || typeof d !== "object" || d.state !== state) return;

    switch (d.type) {
      case MSG.needToken: {
        if (sentToken) return; // one token release per handoff
        const jwt = io.getJwt();
        if (!isWellShapedJwt(jwt)) return fallback();
        sentToken = true;
        popup.postMessage({ type: MSG.token, state, token: jwt }, io.accountsOrigin);
        return;
      }
      case MSG.ok:
        // Only after the token was actually released can an "ok" mean anything.
        if (!sentToken) return;
        return finish("success", () => io.navigate(target));
      case MSG.failed:
      case MSG.error:
        return fallback();
      default:
        return; // unknown type: ignored, not trusted
    }
  });

  popup = io.open(`${io.accountsOrigin}/bridge?state=${encodeURIComponent(state)}`);
  if (!popup) {
    fallback();
    return result;
  }

  timeout = io.setTimer(fallback, HANDOFF_TIMEOUT_MS);
  // The user closing the popup is a cancel, not an error. A grace period lets a message already in flight
  // (ok, then root closes it) win over the poll.
  const poll = () => {
    if (finished) return;
    if (popup?.closed) {
      closedPoll = io.setTimer(() => finish("cancelled"), CLOSED_GRACE_MS);
      return;
    }
    closedPoll = io.setTimer(poll, 400);
  };
  closedPoll = io.setTimer(poll, 400);
  return result;
}
